export type AnalyticsEventName =
  | "level_entered"
  | "first_draw"
  | "level_completed"
  | "level_abandoned"
  | "level_reset"
  | "line_cleared"
  | "undo_used"
  | "first_stuck"
  | "hint_requested"
  | "ad_clicked"
  | "rewarded_ad_requested"
  | "rewarded_ad_closed"
  | "rewarded_ad_rewarded"
  | "interstitial_ad_requested"
  | "interstitial_ad_closed"
  | "settings_changed"
  | "analytics_delivery_failed";

export type AnalyticsFieldValue = string | number | boolean | null;
export type AnalyticsFields = Readonly<Record<string, AnalyticsFieldValue>>;

export interface AnalyticsEvent {
  readonly name: AnalyticsEventName;
  readonly timestampMs: number;
  readonly levelId?: string;
  readonly fields?: AnalyticsFields;
}

export interface AnalyticsSnapshot {
  readonly trackedEventCount: number;
  readonly lastEventName: AnalyticsEventName | null;
  readonly lastLevelId: string | null;
  readonly lastFields: AnalyticsFields | null;
  readonly queuedEventCount: number;
}

export interface AnalyticsService {
  track(event: AnalyticsEvent): void;
  getSnapshot(): AnalyticsSnapshot;
}

export interface AnalyticsTransport {
  send(events: readonly AnalyticsEvent[]): Promise<void>;
}

export type LevelAbandonSource = "destroy" | "pagehide";

export interface LevelAbandonTrackingInput {
  readonly alreadyTracked: boolean;
  readonly elapsedMs: number;
  readonly hasWon: boolean;
}

const SENSITIVE_FIELD_KEYS = new Set(["adUnitId", "adMobAppId", "admobAppId"]);

export class MockAnalyticsService implements AnalyticsService {
  public readonly events: AnalyticsEvent[] = [];

  public track(event: AnalyticsEvent): void {
    this.events.push(cloneSafeAnalyticsEvent(event));
  }

  public getSnapshot(): AnalyticsSnapshot {
    const lastEvent = this.events.at(-1);
    return {
      trackedEventCount: this.events.length,
      lastEventName: lastEvent?.name ?? null,
      lastLevelId: lastEvent?.levelId ?? null,
      lastFields: lastEvent?.fields ?? null,
      queuedEventCount: 0
    };
  }
}

export class QueuedAnalyticsService implements AnalyticsService {
  private readonly queue: AnalyticsEvent[] = [];
  private trackedEventCount = 0;
  private lastEvent: AnalyticsEvent | null = null;
  private activeFlush: Promise<void> | null = null;
  private flushRequested = false;

  public constructor(private readonly transport: AnalyticsTransport) {}

  public track(event: AnalyticsEvent): void {
    const safeEvent = cloneSafeAnalyticsEvent(event);
    this.queue.push(safeEvent);
    this.trackedEventCount += 1;
    this.lastEvent = safeEvent;
  }

  public flush(): Promise<void> {
    this.flushRequested = true;
    if (this.activeFlush) {
      return this.activeFlush;
    }

    const flush = this.drainFlushRequests();
    this.activeFlush = flush;
    void flush.finally(() => {
      if (this.activeFlush === flush) {
        this.activeFlush = null;
      }
    });
    return flush;
  }

  public getSnapshot(): AnalyticsSnapshot {
    return {
      trackedEventCount: this.trackedEventCount,
      lastEventName: this.lastEvent?.name ?? null,
      lastLevelId: this.lastEvent?.levelId ?? null,
      lastFields: this.lastEvent?.fields ?? null,
      queuedEventCount: this.queue.length
    };
  }

  private async drainFlushRequests(): Promise<void> {
    while (this.flushRequested) {
      this.flushRequested = false;
      if (this.queue.length === 0) {
        continue;
      }
      const batch = this.queue.map(cloneSafeAnalyticsEvent);
      try {
        await this.transport.send(batch);
      } catch {
        // Keep the original queue intact. Failure events are not recursively queued
        // through the same failing transport, which keeps retry storage bounded.
        return;
      }
      this.queue.splice(0, batch.length);
    }
  }
}

export function createMockAnalyticsService(): AnalyticsService {
  return new MockAnalyticsService();
}

export function shouldTrackLevelAbandoned(input: LevelAbandonTrackingInput): boolean {
  return !input.alreadyTracked && input.elapsedMs > 0 && !input.hasWon;
}

function cloneSafeAnalyticsEvent(event: AnalyticsEvent): AnalyticsEvent {
  assertAnalyticsEventIsSafe(event);
  return {
    ...event,
    ...(event.fields ? { fields: { ...event.fields } } : {})
  };
}

function assertAnalyticsEventIsSafe(event: AnalyticsEvent): void {
  for (const key of Object.keys(event.fields ?? {})) {
    if (SENSITIVE_FIELD_KEYS.has(key)) {
      throw new Error(`Sensitive analytics field "${key}" must not be tracked.`);
    }
  }
}
